import { ColorTokenSchema } from '@school-journey/shared-types/theme'
import type { TicketedEventBrand } from '@school-journey/shared-types'

/**
 * The attribute the generated brand CSS is scoped to. Put it on the page root;
 * the custom properties cascade to every descendant, so the ShadCN `Button`
 * default variant (`bg-primary text-primary-foreground`) picks the school's
 * color up with no per-button change.
 */
export const BRAND_SCOPE_ATTRIBUTE = 'data-public-event-brand'

/**
 * Build the `<style>` body that repaints `--primary` / `--primary-foreground` /
 * `--ring` inside the branded page, for BOTH theme modes.
 *
 * Why a `<style>` element rather than an inline `style` prop: an inline style
 * can express one set of values, and these pages render inside the dashboard's
 * `next-themes` provider — a dark-mode viewer needs the dark pair. A rule keyed
 * on `.dark` is the only way to carry both in server-rendered markup.
 *
 * Specificity: `[data-public-event-brand]` ties with the `:root` block in
 * globals.css and wins on document order (this element renders inside the
 * body); `.dark [data-public-event-brand]` outranks the `.dark` block outright.
 *
 * Every token is validated against `ColorTokenSchema` before it reaches the
 * string, and the WHOLE brand is rejected if any one fails — a half-applied
 * pair is the one outcome worse than no branding, because `primaryForeground`
 * is what guarantees the label stays legible on `primary`. The schema admits
 * only digits, spaces, dots and percent signs, so nothing that could close the
 * rule or the element can reach the DOM.
 *
 * Returns null when there is no brand (un-branded school) or when validation
 * fails — the caller then renders no style element and the page keeps the
 * platform's default tokens.
 */
export function brandStyleCss(brand: TicketedEventBrand | null | undefined): string | null {
  if (!brand) return null

  const tokens = [brand.light.primary, brand.light.primaryForeground, brand.dark.primary, brand.dark.primaryForeground]
  if (tokens.some(token => !ColorTokenSchema.safeParse(token).success)) return null

  const [lightPrimary, lightForeground, darkPrimary, darkForeground] = tokens as [string, string, string, string]

  // `--ring` follows `--primary` so focus rings match the brand rather than
  // staying on the platform's default grey against a branded button.
  return (
    `[${BRAND_SCOPE_ATTRIBUTE}]{--primary:${lightPrimary};--primary-foreground:${lightForeground};--ring:${lightPrimary}}` +
    `.dark [${BRAND_SCOPE_ATTRIBUTE}]{--primary:${darkPrimary};--primary-foreground:${darkForeground};--ring:${darkPrimary}}`
  )
}
